import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useApi } from "@excellent/ui/api";
import { formatDateTime } from "@excellent/ui";
import { CHAIN_ANCHOR_TARGETS } from "@excellent/core/verification-chain-anchor-display";
import { VERIFICATION_POSTURE_QUESTIONS, postureCoverage } from "@excellent/core/verification-posture-statement";
import {
  OUT_OF_BAND_KEY_SOURCES,
} from "@excellent/core/verification-trust-root";
import { href } from "../nav";
import { useVerificationEnvOptional } from "../env-provider";
import { verificationCanonicalClients } from "../api/canonical-clients";
import {
  ActionButton,
  ButtonRow,
  Callout,
  DataTable,
  FilterField,
  FilterToolbar,
  ListSearch,
  Mono,
  PageActions,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
} from "../ui";
import { PageError, PageFrame, PageLink, PagePending, type VerificationPageProps } from "./page-frame";

export const API_OPEN_ENDPOINT_ACTION = "api.open-endpoint";

const RUNTIME_OPEN_RUNBOOK_ACTION = "runtime.open-runbook";

function useLastFetchedAt(data: unknown): string | null {
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  useEffect(() => {
    if (data) setLastFetchedAt(new Date().toISOString());
  }, [data]);
  return lastFetchedAt;
}

function LastFetched({ at, stale }: { at: string | null; stale?: boolean }) {
  if (!at) return null;
  return (
    <Callout tone={stale ? "unknown" : "neutral"} title={stale ? "Showing stale data" : "Real data loaded"}>
      Last fetched {formatDateTime(at)}.
    </Callout>
  );
}

function withDemoFallback(node: ReactNode): ReactNode {
  return <>{node}</>;
}

function MaybeDemo({ children }: { children: ReactNode }) {
  const existing = null;
  if (existing) return <>{children}</>;
  return withDemoFallback(children);
}

const API_LEDE =
  "The self-hosted API's declared operations and this host's current mount state. Credential management remains unavailable here.";

export function ApiPage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <ApiPageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function ApiPageInner({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const catalogApi = useApi(
    verificationCanonicalClients.catalogGet.operation,
    ready && environment ? { query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(catalogApi.data);

  const endpoints = catalogApi.data?.endpoints ?? [];
  const selected = endpoints.find((row) => row.id === selectedId) ?? null;
  const rows = endpoints.filter((row) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return (
      row.id.toLowerCase().includes(needle) ||
      row.path.toLowerCase().includes(needle) ||
      row.summary.toLowerCase().includes(needle)
    );
  });

  if (env && !ready) {
    return (
      <PageFrame page="api" wide title="API & SDK" lede={API_LEDE}>
        <PagePending title="API catalog not loaded">Loading the served endpoint inventory for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="api" wide title="API & SDK" lede={API_LEDE}>
        <PagePending title="API catalog not loaded">API catalog rows are environment-scoped. Pick an environment to open endpoint docs.</PagePending>
      </PageFrame>
    );
  }

  if (catalogApi.error) {
    return (
      <PageFrame page="api" wide title="API & SDK" lede={API_LEDE}>
        <PageError title="API catalog failed to load" error={catalogApi.error} onRetry={() => void catalogApi.mutate()} />
      </PageFrame>
    );
  }

  if (catalogApi.isLoading || !catalogApi.data) {
    return (
      <PageFrame page="api" wide title="API & SDK" lede={API_LEDE}>
        <PagePending title="Loading API catalog">Loading {environment === "demo" ? "Demo" : "Real"} endpoint inventory.</PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="api"
      wide
      title="API & SDK"
      lede={API_LEDE}
    >
      <LastFetched at={lastFetchedAt} stale={Boolean(catalogApi.error && catalogApi.data)} />
      <div className="vp-callout" data-tone={catalogApi.data.surface.mounted ? "accent" : "unknown"}>
        <div className="vp-callout-title">
          {catalogApi.data.surface.mounted ? "Public API mounted on this host" : "Public API not mounted on this host"}
        </div>
        <p className="vp-callout-body">
          The catalog below comes from the server's authoritative operation descriptor. The separate{" "}
          <span className="vp-mono">{catalogApi.data.surface.mountPath}</span> surface is{" "}
          {catalogApi.data.surface.mounted ? "enabled with engine handlers" : "disabled until EXCELLENT_VERIFICATION_API=1"}.
        </p>
      </div>

      <Section
        title="Operations"
        note="Every operation this deployment's catalogue declares. Rows separate three states: included and working, included but refused with 501, and engine-only operations no token can reach."
      >
        <DataTable
          toolbar={
            <FilterToolbar>
              <FilterField label="Search" wide>
                <ListSearch
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search operations, paths, descriptions…"
                  aria-label="Search operations"
                />
              </FilterField>
            </FilterToolbar>
          }
          rows={rows}
          rowKey={(row) => row.id}
          columns={[
            {
              key: "availability",
              header: "Public surface",
              // Three states, not two. `served` and `implemented` are separate
              // facts: withheld means no token can reach the operation at all,
              // while declared-not-implemented means it is reachable, scoped
              // and documented and the engine answers 501 with a reason. An
              // operation this product will not perform must never render the
              // same as one it does.
              render: (row) =>
                !row.served ? (
                  <ToneBadge tone="unknown">withheld</ToneBadge>
                ) : !row.implemented ? (
                  <ToneBadge tone="waived">not implemented</ToneBadge>
                ) : (
                  <ToneBadge tone="neutral">included</ToneBadge>
                ),
            },
            {
              key: "operation",
              header: "Operation",
              render: (row) => <Mono>{row.id}</Mono>,
            },
            {
              key: "method",
              header: "Method",
              render: (row) => (
                <ToneBadge tone={row.method === "GET" ? "neutral" : "accent"}>{row.method}</ToneBadge>
              ),
            },
            {
              key: "path",
              header: "Path",
              render: (row) => <Mono>{row.path}</Mono>,
            },
            {
              key: "scope",
              header: "Required scope",
              render: (row) => <Mono>{row.scope}</Mono>,
            },
            {
              key: "stability",
              header: "Stability",
              render: (row) => (
                <ToneBadge tone={row.stability === "stable" ? "pass" : "unknown"}>{row.stability}</ToneBadge>
              ),
            },
            {
              key: "docs",
              header: "Docs",
              render: (row) => (
                <ActionButton
                  actionId={API_OPEN_ENDPOINT_ACTION}
                  data-action-id={API_OPEN_ENDPOINT_ACTION}
                  data-endpoint-id={row.id}
                  onClick={() => setSelectedId(row.id)}
                >
                  Open docs
                </ActionButton>
              ),
            },
          ]}
        />
      </Section>

      <Section title="API credentials" note="Credential records live in the durable verification API token store.">
        <Callout tone="unknown" title="Credential management is not available in this UI">
          This page cannot create, rotate, or revoke a bearer credential. No token metadata shown elsewhere in the verification workspace should be used for authentication.
        </Callout>
      </Section>

      {selected ? (
        <section className="vp-section" data-endpoint-docs={selected.id}>
          <div className="vp-section-head">
            <h2 className="vp-h2">Endpoint docs</h2>
            <span className="vp-section-note">Opened from the catalog row — not a toast.</span>
          </div>
          <div className="vp-card" data-demo-scoped={catalogApi.data.provenance.demoScoped ? "true" : undefined}>
            <div className="vp-card-head">
              <div>
                <h2 className="vp-card-title">
                  <span className="vp-mono">{selected.method}</span> {selected.path}
                </h2>
                <p className="vp-card-sub">
                  <span className="vp-mono">{selected.id}</span> · scope {selected.scope}
                </p>
              </div>
            </div>
            <div className="vp-card-body">
              <p style={{ margin: "0", lineHeight: 1.6 }}>{selected.summary}</p>
              <div className="vp-fields" style={{ marginTop: "calc(var(--spacing) * 3)" }}>
                <div className="vp-field">
                  <div className="vp-field-label">Stability</div>
                  <div className="vp-field-value">{selected.stability}</div>
                </div>
                <div className="vp-field">
                  <div className="vp-field-label">Resource</div>
                  <div className="vp-field-value">{selected.resource}</div>
                </div>
                <div className="vp-field">
                  <div className="vp-field-label">Public surface</div>
                  <div className="vp-field-value">{selected.served ? "Included" : "Withheld"}</div>
                </div>
                <div className="vp-field">
                  <div className="vp-field-label">Implemented</div>
                  <div className="vp-field-value">{selected.implemented ? "Yes" : "No — refuses with 501"}</div>
                </div>
              </div>
              {selected.withheldReason ? (
                <Callout tone="unknown" title="Why this operation is withheld">
                  {selected.withheldReason}
                </Callout>
              ) : null}
              {selected.notImplementedReason ? (
                <Callout tone="waived" title="This operation is documented and will not run">
                  {selected.notImplementedReason}
                </Callout>
              ) : null}
            </div>
          </div>
        </section>
      ) : (
        <PagePending title="No endpoint selected">Choose a catalog row to open its docs.</PagePending>
      )}

      <p className="vp-help" style={{ marginTop: "calc(var(--spacing) * 4)" }}>
        Signing-key fingerprints and rotation live on{" "}
        <PageLink onNavigate={onNavigate} to="runtime">
          Runtime & keys
        </PageLink>
        .
      </p>
    </PageFrame>
  );
}

const TRUST_LEDE =
  "What an outside party can check without trusting us — and precisely where that stops.";

const POSTURE_LEDE =
  "Declared product-design answers, not observations of this deployment. Unavailable capabilities remain explicitly not yet.";

export function TrustPage({ onNavigate }: VerificationPageProps) {
  const anchorPosture = useMemo(
    () => VERIFICATION_POSTURE_QUESTIONS.find((q) => q.id === "anchoring-external") ?? null,
    [],
  );

  return (
    <PageFrame page="trust" title="Trust root" lede={TRUST_LEDE}>
      <section className="vp-section">
        <div className="vp-section-head">
          <h2 className="vp-h2">Receipt integrity is not yet independently verifiable</h2>
        </div>
        <div className="vp-grid" data-cols="2">
          <div className="vp-card">
            <h3 className="vp-card-title">Independent verifier unavailable</h3>
            <p className="vp-card-sub">
              Current metadata packs do not include a separately distributed verifier or a signed receipt envelope.
            </p>
            <ToneBadge tone="unknown">open</ToneBadge>
          </div>
          <div className="vp-card">
            <h3 className="vp-card-title">Independent key source unavailable</h3>
            <p className="vp-card-sub">
              Reported key identifiers are metadata only until a verifier can obtain trust material independently of Excellent.
            </p>
            <ToneBadge tone="unknown">open</ToneBadge>
          </div>
        </div>
      </section>

      <section className="vp-section">
        <div className="vp-section-head">
          <h2 className="vp-h2">Supported target types, not active configuration</h2>
        </div>
        <div className="vp-card">
          <ul>
            {CHAIN_ANCHOR_TARGETS.map((t) => (
              <li key={t}>
                <span className="vp-mono">{t}</span>
              </li>
            ))}
          </ul>
          <p className="vp-row-sub">
            Planned independent key sources: {OUT_OF_BAND_KEY_SOURCES.join(", ")}. These names do not establish an active trust root or prove that a receipt was signed.
          </p>
        </div>
      </section>

      <section className="vp-section">
        <div className="vp-section-head">
          <h2 className="vp-h2">Not closed: the two stated ceilings</h2>
        </div>
        <div className="vp-callout" data-tone="unknown">
          <div className="vp-callout-title">Signed envelopes and external anchors are not shipped here</div>
          <p className="vp-callout-body">The current pack is metadata only. No active trust-root mode or vendor-independent assurance is inferred.</p>
        </div>
        <div className="vp-callout" data-tone="unknown" style={{ marginTop: "calc(var(--spacing) * 3)" }}>
          <div className="vp-callout-title">Rollback is not detectable from inside a pack</div>
          <p className="vp-callout-body">{anchorPosture?.answer ?? "External freshness proof is unavailable."}</p>
        </div>
      </section>

      <ButtonRow>
        <ActionButton
          actionId={RUNTIME_OPEN_RUNBOOK_ACTION}
          data-action-id={RUNTIME_OPEN_RUNBOOK_ACTION}
          variant="primary"
          onClick={() => onNavigate(href("runtime"))}
        >
          Open runtime & keys
        </ActionButton>
      </ButtonRow>
    </PageFrame>
  );
}

export function PosturePage({ onNavigate }: VerificationPageProps) {
  const coverage = useMemo(() => postureCoverage(), []);
  const questions = VERIFICATION_POSTURE_QUESTIONS;

  return (
    <PageFrame page="posture" title="Product design posture" lede={POSTURE_LEDE}>
      <Callout tone="unknown" title="Not observed deployment posture">
        These declarations describe intended product boundaries. Runtime checks and configured trust material must be inspected separately before treating an answer as true for this node.
      </Callout>
      <StatGrid cols={4}>
        <Stat label="Questions" value={coverage.total} />
        <Stat label="Declared answers" value={coverage.answered} />
        <Stat label="Answered with caveat" value={coverage.caveated} tone="unknown" />
        <Stat label="Not yet" value={coverage.notYet} tone={coverage.notYet > 0 ? "fail" : "pass"} />
      </StatGrid>

      {coverage.notYet > 0 ? (
        <div className="vp-callout" data-tone="fail" style={{ marginTop: "calc(var(--spacing) * 4.5)" }}>
          <div className="vp-callout-title">{coverage.notYet} question(s) we cannot answer yet</div>
          <p className="vp-callout-body">
            These are listed rather than omitted. An unanswered row is a fact about the product; a missing row is a fact about the page.
          </p>
        </div>
      ) : null}

      {questions.map((q) => (
        <section className="vp-section" key={q.id}>
          <div className="vp-section-head">
            <h2 className="vp-h2">{q.question}</h2>
            <span className="vp-mono vp-section-note">{q.id}</span>
          </div>
          <div className="vp-card">
            <ToneBadge
              tone={q.status === "answered" ? "neutral" : q.status === "answered-with-caveat" ? "unknown" : "fail"}
            >
              {q.status === "answered"
                ? "declared"
                : q.status === "answered-with-caveat"
                  ? "answered, with a stated ceiling"
                  : "not yet"}
            </ToneBadge>
            <p style={{ marginTop: "calc(var(--spacing) * 2.5)", lineHeight: 1.6 }}>{q.answer}</p>
            {q.caveat ? (
              <div className="vp-callout" data-tone="unknown" style={{ marginTop: "calc(var(--spacing) * 2.5)" }}>
                <div className="vp-callout-title">The ceiling on that answer</div>
                <p className="vp-callout-body">{q.caveat}</p>
              </div>
            ) : null}
            {q.runtimeModes.length > 0 ? (
              <p className="vp-row-sub">Varies by runtime mode: {q.runtimeModes.join(", ")}</p>
            ) : null}
          </div>
        </section>
      ))}

      <ButtonRow>
        <ActionButton actionId="nav.runtime" variant="primary" onClick={() => onNavigate(href("runtime"))}>
          Open runtime & keys
        </ActionButton>
      </ButtonRow>
    </PageFrame>
  );
}
